import React from 'react'
/**
 * Props for the PaginationElement component.
 */
interface PaginationProps {
    /** The current active page number. */
    currentPage: number
    /** The total number of pages available. */
    totalPages: number
    /** Function to handle changing to a specific page. */
    onPageChange: (pageNumber: number) => void
    /** Function to navigate to the first page. */
    onFirstPage: () => void
    /** Function to navigate to the last page. */
    onLastPage: () => void
    /** Function to navigate to the previous page. */
    onPrevPage: () => void
    /** Function to navigate to the next page. */
    onNextPage: () => void
}

/**
 * A pagination component that provides navigation for multiple pages.
 *
 * @component
 * @param {PaginationProps} props - The props for the pagination component.
 * @returns {JSX.Element | null} The rendered pagination component or null if only one page exists.
 */
const PaginationElement: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    onFirstPage,
    onLastPage,
    onPrevPage,
    onNextPage
}) => {
    const pageNumbers: number[] = []
    const maxVisiblePages = 3 // Maximum number of visible page numbers

    for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i)
    }
    /**
     * Renders the page number buttons dynamically based on the total pages and current page.
     *
     * @returns {JSX.Element[]} An array of page number elements.
     */
    const renderPageNumbers = () => {
        if (totalPages <= maxVisiblePages) {
            return pageNumbers.map((number) => (
                <div
                    key={number}
                    className={`${
                        currentPage === number
                            ? 'bg-primary text-white rounded px-[11px] py-[3.5px] cursor-pointer'
                            : 'border-0 rounded px-[11px] py-[6px] text-xs font-semibold cursor-pointer'
                    }`}
                    onClick={() => onPageChange(number)}
                >
                    <span>{number}</span>
                </div>
            ))
        } else {
            const middleIndex = Math.ceil(maxVisiblePages / 2)
            // Calculate the range of visible page numbers
            let startPage = currentPage - middleIndex + 1
            let endPage = currentPage + middleIndex - 1

            if (startPage < 1) {
                startPage = 1
                endPage = maxVisiblePages
            } else if (endPage > totalPages) {
                endPage = totalPages
                startPage = totalPages - maxVisiblePages + 1
            }

            const pageElements = []

            for (let i = startPage; i <= endPage; i++) {
                pageElements.push(
                    <div
                        key={i}
                        className={`${
                            currentPage === i
                                ? 'bg-primary text-white rounded px-[11px] py-[3.5px] cursor-pointer'
                                : 'border-0 rounded px-[11px] py-[6px] text-xs font-semibold cursor-pointer'
                        }`}
                        onClick={() => onPageChange(i)}
                    >
                        <span>{i}</span>
                    </div>
                )
            }

            return pageElements
        }
    }

    return totalPages > 1 ? (
        <div className='flex items-center gap-4'>
            <p className={`py-1 text-sm font-semibold text-[#2D3748]`}>
                <span className={`cursor-pointer ${currentPage === 1 ? 'disabled' : ''}`} onClick={onFirstPage}>
                    {'First'}
                </span>
            </p>
            <p className={`py-1 text-sm font-semibold text-[#2D3748] `}>
                <span className={`cursor-pointer ${currentPage === 1 ? 'disabled' : ''}`} onClick={onPrevPage}>
                    {'Prev'}
                </span>
            </p>
            {renderPageNumbers()}
            <p className={`py-1 text-sm font-semibold text-[#2D3748]`}>
                <span className={`cursor-pointer ${currentPage === totalPages ? 'disabled' : ''}`} onClick={onNextPage}>
                    {'Next'}
                </span>
            </p>
            <p className={`py-1 text-sm font-semibold text-[#2D3748]`}>
                <span className={`cursor-pointer ${currentPage === totalPages ? 'disabled' : ''}`} onClick={onLastPage}>
                    {'Last'}
                </span>
            </p>
        </div>
    ) : null
}

export default PaginationElement
